import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useGame } from '../../context/GameContext';
import { formatNumber } from '../../utils/formatters';
import * as FiIcons from 'react-icons/fi';
import { GameService } from '../../services/gameService';

const { FiMenu, FiSettings, FiSprout, FiStar, FiZap, FiSearch } = FiIcons;

const PAGE_TITLES = {
  '/': 'Today',
  '/plan': 'Plan',
  '/studio': 'Studio',
  '/garden': 'Garden',
  '/journal': 'Journal',
  '/membership': 'Membership',
  '/settings': 'Settings',
  '/focus': 'Studio',
  '/classic/garden': 'Classic Garden',
};

export default function TopBar({ title, onOpenMobileMenu, isFullWidth = false }) {
  const { state } = useGame();
  const navigate = useNavigate();

  const totalSps = GameService.calculateSPS(state.planters, state.gardeners, state.autoLevel, state.sps, state.compost);

  const handleSettingsClick = () => {
    navigate('/settings');
  };

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
      className={`
        sticky top-0 z-20 bg-white/90 backdrop-blur-sm border-b border-sage/20 shadow-sm
        ${isFullWidth ? 'fixed left-0 right-0' : ''}
      `}
    >
      <div className="flex items-center justify-between px-4 py-3 h-16">
        {/* Left Side */}
        <div className="flex items-center space-x-3">
          <button
            onClick={onOpenMobileMenu}
            className="lg:hidden p-2 rounded-xl bg-sage/10 hover:bg-sage/20 transition-colors duration-200 touch-manipulation"
            aria-label="Open navigation menu"
          >
            <SafeIcon icon={FiMenu} className="w-5 h-5 text-charcoal" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-cormorant font-bold text-charcoal truncate">
              {PAGE_TITLES[window.location.pathname] || title}
            </h1>
            <p className="text-xs text-charcoal/60 font-dm-sans hidden sm:block">
              Welcome, {state.profile.name}
            </p>
          </div>
        </div>

        {/* Right Side */}
        <div className="flex items-center space-x-2 flex-shrink-0">
          {/* Command Hint Desktop */}
          <button 
            type="button" 
            className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-white border border-sage/20 rounded-lg text-sm text-charcoal/50 font-dm-sans mr-2 cursor-pointer hover:bg-sage/5 transition-colors" 
            onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))}
          >
            <SafeIcon icon={FiSearch} className="w-4 h-4" />
            <span>Search</span>
            <kbd className="font-mono text-[10px] bg-sage/10 px-1.5 rounded text-charcoal/60 font-bold border border-sage/20">⌘K</kbd>
          </button>
          
          {/* Wallet Display */}
          <div className="hidden items-center space-x-2 bg-gradient-to-r from-sage/10 to-transparent p-1 rounded-xl border border-sage/20" aria-hidden="true">
            <div className="flex items-center space-x-1.5 bg-white px-2 py-1 rounded-lg">
              <SafeIcon icon={FiSprout} className="w-4 h-4 text-sage" />
              <span className="font-dm-sans font-semibold text-charcoal text-sm">
                {formatNumber(state.seeds)}
              </span>
            </div>
            <div className="flex items-center space-x-1.5 px-2 py-1">
              <SafeIcon icon={FiStar} className="w-4 h-4 text-goldenrod" />
              <span className="font-dm-sans font-semibold text-charcoal text-sm">
                {formatNumber(totalSps)}
              </span>
            </div>
            <div className="flex items-center space-x-1.5 px-2 py-1">
              <SafeIcon icon={FiZap} className="w-4 h-4 text-lavender" />
              <span className="font-dm-sans font-semibold text-charcoal text-sm">
                {state.prestigeLevel}
              </span>
            </div>
          </div>

          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={handleSettingsClick}
            className="w-8 h-8 rounded-full bg-white border border-sage/20 flex items-center justify-center hover:bg-sage/10 transition-colors"
            aria-label="Open settings"
          >
            <SafeIcon icon={FiSettings} className="w-4 h-4 text-charcoal" />
          </motion.button>
        </div>
      </div>
    </motion.header>
  );
}